import { Calendar, Sparkles, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { useAnalytics } from "@/hooks/useAnalytics";

const DEMO_BOOKING_URL = "https://viral.partners/umow-demo";

type DemoStage = "early" | "mid";

interface DemoBookingModalProps {
  open: boolean;
  onClose: () => void;
  stage: DemoStage;
}

const STAGE_COPY: Record<DemoStage, { titleKey: string; descKey: string }> = {
  early: {
    titleKey: "demoBooking.early.title",
    descKey: "demoBooking.early.description",
  },
  mid: {
    titleKey: "demoBooking.mid.title",
    descKey: "demoBooking.mid.description",
  },
};

export function DemoBookingModal({ open, onClose, stage }: DemoBookingModalProps) {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();

  const handleBookDemo = () => {
    trackEvent("demo_prompt_clicked", { stage });
    window.open(DEMO_BOOKING_URL, "_blank", "noopener,noreferrer");
    onClose();
  };

  const handleDismiss = () => {
    trackEvent("demo_prompt_dismissed", { stage });
    onClose();
  };

  const copy = STAGE_COPY[stage];

  return (
    <Dialog open={open} onOpenChange={handleDismiss}>
      <DialogContent className="bg-[#1a1c22] border border-white/10 text-white max-w-md">
        <DialogHeader className="items-center text-center">
          <div className="w-14 h-14 rounded-full bg-orange-500/10 flex items-center justify-center mb-3 mx-auto">
            <Sparkles className="w-7 h-7 text-orange-400" />
          </div>
          <DialogTitle className="text-xl font-semibold">{t(copy.titleKey)}</DialogTitle>
          <DialogDescription className="text-white/60 text-sm mt-2">
            {t(copy.descKey)}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          <Button
            onClick={handleBookDemo}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
          >
            <Calendar className="w-4 h-4 mr-2" />
            {t("demoBooking.book")}
          </Button>
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="w-full text-white/50 hover:text-white hover:bg-white/5"
          >
            {t("demoBooking.later")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
